import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useLang } from "../i18n";

export function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState("pending");
  const [orderId, setOrderId] = useState(null);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrderId(data.order_id);
          clearCart();
          return;
        }
      } catch (e) { /* retry */ }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-5 py-28 text-center" data-testid="payment-success-page">
      {status === "pending" ? (
        <div className="flex flex-col items-center gap-4" data-testid="payment-pending">
          <Loader2 className="animate-spin text-honey" size={40} />
          <p className="text-cocoa/60">{t("pay.pending")}</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
          <CheckCircle2 className="mx-auto text-sage" size={56} />
          <h1 className="mt-6 font-serif text-4xl font-bold" data-testid="payment-success-title">{t("pay.success.title")}</h1>
          <p className="mt-4 text-cocoa/60">{t("pay.success.sub")}</p>
          {orderId && (
            <p className="mt-6 font-mono text-sm bg-cream border border-[#E5D9C8] inline-block px-4 py-2" data-testid="payment-order-id">
              {t("pay.success.order")}: <span className="text-honey font-semibold">#{orderId.slice(0, 8)}</span>
            </p>
          )}
          <div className="mt-8">
            <Link to="/shop" data-testid="payment-success-cta" className="inline-block bg-bark text-parchment px-8 py-4 font-medium hover:bg-honey transition-colors duration-300">
              {t("pay.success.cta")}
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <div className="max-w-lg mx-auto px-5 py-28 text-center" data-testid="payment-cancel-page">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
        <XCircle className="mx-auto text-terra" size={56} />
        <h1 className="mt-6 font-serif text-4xl font-bold" data-testid="payment-cancel-title">{t("pay.cancel.title")}</h1>
        <p className="mt-4 text-cocoa/60">{t("pay.cancel.sub")}</p>
        <div className="mt-8">
          <Link to="/checkout" data-testid="payment-cancel-cta" className="inline-block bg-bark text-parchment px-8 py-4 font-medium hover:bg-honey transition-colors duration-300">
            {t("pay.cancel.cta")}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
